<template>
  <div>
    <v-container :class="containerClass">
      <h3>Receipt Details</h3>
      <v-data-table
        dense
        :headers="headers"
        :items-per-page="-1"
        :items="editedItem"
        hide-default-footer
        class="elevation-1"
      >
        <template v-slot:top>
          <div class="d-flex justify-end">
            <v-checkbox
              class="recon-details-calculate-percentage-flag"
              v-model="calculatePercentage"
              label="Use % Breakdown"
            ></v-checkbox>
            <v-btn
              class="mx-2 recon-details-add-new-item"
              color="primary" fab icon x-small
              @click="addOne"
              v-if="amountToNumber(amountRemaining) > 0 || transItem.isCorrection"
            >
              <v-icon>
                {{icons.mdiPlus}}
              </v-icon>
          </v-btn>
          </div>
        </template>

        <template v-slot:[`item.taxDistrict`]="{ item }">
          <v-select
            v-model="item.taxDistrict"
            :items="taxDistricts"
            v-if="transItem.isApportionLikeTax"
            item-text="taxDistrictDesc"
            item-value="_id"
            dense
            single-line
            @blur="somethingChanged"
          ></v-select>
        </template>

        <template v-slot:[`item.fund`]="{ item, index }">
          <v-autocomplete
            v-model="item.fund"
            v-if="!transItem.isApportionLikeTax"
            label="Fund"
            item-text="fundDesc"
            item-value="_id"
            :items="funds"
            :rules="fundAltKeyRules(item)"
            dense
            single-line
            @blur="somethingChanged"
            :ref="rowRef('isFundRef', index)"
            @keydown.tab.prevent="handleTab(
              [rowRef('isUnitRef', index), rowRef('isAmountRef', index)])"
          ></v-autocomplete>
        </template>

        <template v-slot:[`item.source`]="{ item, index }">
          <v-autocomplete
            v-model="item.source"
            label="Fund"
            item-text="sourceDesc"
            item-value="_id"
            :items="sources"
            :rules="sourceAltKeyRules(item)"
            dense
            single-line
            @blur="somethingChanged"
            :ref="rowRef('isSourceRef', index)"
            @keydown.tab.prevent="handleTab(rowRef('isFundRef', index))"
          ></v-autocomplete>
        </template>

        <template v-slot:[`item.Dynamic`]="{ item, index }" v-if="!transItem.isApportionLikeTax">
            <v-autocomplete
            label="new School/Unit Code"
            v-model="item.taxUnitCode"
            v-if="!transItem.isApportionLikeTax && unitCodeNewMethodFalg"
            item-text="unitDesc"
            item-value="unitCode"
            :items="unitCodes"
            :disabled="isRequiredUnitChecked(item)"
            :rules="requiredUnitRules(item)"
            dense
            single-line
            @blur="somethingChanged"
            :ref="rowRef('isUnitRef', index)"
            @keydown.tab.prevent="handleTab(rowRef('isAmountRef', index))"
          ></v-autocomplete>
          <v-autocomplete
            label="School/Unit Code"
            v-model="item.unit"
            v-if="!transItem.isApportionLikeTax && !unitCodeNewMethodFalg"
            item-text="unitDesc"
            item-value="_id"
            :items="unitCodes"
            :disabled="isRequiredUnitChecked(item)"
            :rules="requiredUnitRules(item)"
            dense
            single-line
            @blur="somethingChanged"
            :ref="rowRef('isUnitRef', index)"
            @keydown.tab.prevent="handleTab(rowRef('isAmountRef', index))"
          ></v-autocomplete>
        </template>

        <template v-slot:[`item.percentageBreakdown`]="{ item }">
          <v-text-field
            dense
            single-line
            label="Percentage Breakdown"
            v-model="item.percentageBreakdown"
            v-if="calculatePercentage"
            @blur="somethingChanged(), calculateAmount()"
          ></v-text-field>
        </template>

        <template v-slot:[`item.amount`]="{ item, index }">
          <v-text-field
            dense
            single-line
            label="Amount"
            v-model="item.amount"
            @blur="somethingChanged"
            @keydown="shortcutHandler($event, index);"
            @keydown.enter="somethingChanged"
            @focus="$event.target.select()"
            :ref="rowRef('isAmountRef', index)"
          ></v-text-field>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-icon
            small class="mr-2"
            tabindex="-1"
            @click="removeOne(item)"
          >
            {{icons.mdiDelete}}
          </v-icon>
        </template>
        <template slot="body.append">
          <tr class="blue--text">
              <td id="total-trans-amount">
                Total Reconciliation Amount: ${{ amountFormat(amountRemaining) }}
              </td>
              <td id="total-percentage"
                v-if="calculatePercentage">
                Total Remaining Percentage: {{amountFormat(percentageRemaining)}}%
              </td>
          </tr>
        </template>
      </v-data-table>
    </v-container>
  </div>
</template>

<script>
import {
  mapGetters,
  mapState,
  mapActions,
} from 'vuex';
import {
  mdiPlus,
  mdiDelete,
} from '@mdi/js';
import {
  amountFormat,
  amountRoundOfToClosestDecimal,
  amountToNumber,
  extractId,
  isTabOrPlus,
} from '../../../util/shared/vue-global';
import {
  sourceAltKeyRuleMessage, fundAltKeyRuleMessage, requiredUnitRuleMessage,
} from './miscTransaction.util';

const defaultItem = {
  fund: null,
  taxDistrict: null,
  source: null,
  unit: null,
  amount: 0,
  taxUnitCode: null,
};

export default {
  name: 'MiscDetails',
  created() {
    this.loadConfig({ requiredScope: 'apportionmentConfig' });
  },
  props: {
    baseCrudKey: {
      type: String,
      default: 'miscTrans-dialog',
    },
    value: {
      type: Array,
      default: () => [],
    },
    funds: {
      type: Array,
      default: () => [],
    },
    sources: {
      type: Array,
      default: () => [],
    },
    unitCodes: {
      type: Array,
      default: () => [],
    },
    transItem: {
      type: Object,
      default: () => {},
    },
    taxDistricts: {
      type: Array,
      default: () => [],
    },
    amount: [Number, String],
    unitCodeNewMethodFalg: { // #migrateUnitCode -> remove unitCodeNewMethodFalg after migration
      type: Boolean,
      default: false,
    },
    taxUnits: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    icons: {
      mdiPlus,
      mdiDelete,
    },
    defaultHeaders: [
      { text: 'Source', value: 'source' },
      { text: 'Fund', value: 'fund' },
      { text: 'Tax District', value: 'taxDistrict' },
      { text: 'Unit', value: 'Dynamic' },
      { text: 'Percentage Breakdown', value: 'percentageBreakdown' },
      { text: 'Amount', value: 'amount' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
  }),
  watch: {
    amount() {
      if (this.calculatePercentage) {
        this.calculateAmount();
        this.somethingChanged();
      }
    },
  },
  computed: {
    ...mapGetters('user', ['getSettings']),
    ...mapGetters('base/crud', ['criteria', 'item', 'items']),
    ...mapState({
      apportionmentSettingItem: (state) => state.SystemConfig.apportionmentSettingItem || {},
    }),
    headers() {
      let updatedHeaders = [...this.defaultHeaders];
      if (!this.transItem.isApportionLikeTax) {
        updatedHeaders = updatedHeaders.filter((h) => h.value !== 'taxDistrict');
      }
      if (this.transItem.isCalculatePercentage === false) {
        updatedHeaders = updatedHeaders.filter((h) => h.value !== 'percentageBreakdown');
      }
      if (this.transItem.isApportionLikeTax) {
        updatedHeaders = updatedHeaders.filter((h) => h.value !== 'fund' && h.value !== 'Dynamic');
      }
      return updatedHeaders;
    },
    editedItem: {
      get() {
        const newVal = JSON.parse(JSON.stringify(this.value));
        const updatedValue = newVal.map(this.loadInitialValues);
        return updatedValue;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    calculatePercentage: {
      get() {
        return this.transItem.isCalculatePercentage;
      },
      set(value) {
        this.$emit('setCalculatePercentage', value);
      },
    },
    amountRemaining() {
      let totalRemainingReconAmount = amountToNumber(this.amount);
      for (let i = 0; i < this.editedItem.length; i += 1) {
        totalRemainingReconAmount = amountToNumber(amountToNumber(totalRemainingReconAmount)
         - amountToNumber(this.editedItem[i].amount));
      }
      this.$emit('totalRemainingReconAmount', totalRemainingReconAmount);
      return totalRemainingReconAmount;
    },
    percentageRemaining() {
      let remainingPercent = 100;
      for (let i = 0; i < this.editedItem.length; i += 1) {
        if (this.editedItem[i].percentageBreakdown) {
          remainingPercent -= amountRoundOfToClosestDecimal(
            this.editedItem[i].percentageBreakdown, 5,
          );
        }
      }
      return remainingPercent;
    },
    containerClass() {
      return this.getSettings.lightdark === 'light' ? 'grey lighten-5 mb-6' : '';
    },
  },
  methods: {
    ...mapActions('SystemConfig', ['loadConfig']),
    // refs declared inside the data-table rows are registered per row, so a shared name
    // would collect every row's field into one $refs array and tabbing would land on an
    // arbitrary row (see the source -> fund tab order). Key each ref by its row index.
    rowRef(name, index) {
      return `${name}-${index}`;
    },
    resolveRef(name) {
      const ref = this.$refs[name];
      if (Array.isArray(ref)) {
        // rows are torn down and rebuilt whenever somethingChanged() re-emits the array,
        // and Vue keeps the destroyed instance in $refs until the next removal pass.
        return ref.find((r) => r && !r._isDestroyed) || ref[0];
      }
      return ref;
    },
    handleTab(nextField) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          let itemRefFocus;
          if (Array.isArray(nextField)) {
            const itemRef0 = this.resolveRef(nextField[0]);
            if (!itemRef0 || itemRef0.isDisabled) {
              itemRefFocus = this.resolveRef(nextField[1]);
            } else {
              itemRefFocus = itemRef0;
            }
          } else {
            itemRefFocus = this.resolveRef(nextField);
          }
          if (itemRefFocus) {
            itemRefFocus.focus();
          }
        });
      });
    },
    amountFormat,
    amountToNumber,
    amountRoundOfToClosestDecimal,
    loadInitialValues(item) {
      return {
        ...item,
        amount: amountFormat(amountToNumber(item.amount)),
      };
    },
    addOne() {
      this.$emit('input', [
        ...this.editedItem,
        {
          ...defaultItem,
        },
      ]);
      return true;
    },
    removeOne(item) {
      this.$emit('input', [
        ...this.editedItem.filter((candidate) => item !== candidate),
      ]);
    },
    somethingChanged() {
      this.$emit('input', this.editedItem);
    },
    // Shared with TransactionList.vue's pre-save gate (miscTransaction.util.js) so the
    // inline rule and the save-blocking check can never drift out of sync.
    sourceAltKeyRules(item) {
      return [() => sourceAltKeyRuleMessage(item, {
        sources: this.sources,
        unitCodes: this.unitCodes,
        useNewUnitCodeSetup: this.unitCodeNewMethodFalg,
        isVoid: this.transItem.isVoid,
      }) || true];
    },
    fundAltKeyRules(item) {
      return [() => fundAltKeyRuleMessage(item, {
        funds: this.funds,
        unitCodes: this.unitCodes,
        taxUnits: this.taxUnits,
        useNewUnitCodeSetup: this.unitCodeNewMethodFalg,
        isVoid: this.transItem.isVoid,
        allowFundExceptions: this.apportionmentSettingItem.allowExceptionsOnSpecialUnitCodeMiscReceipts,
      }) || true];
    },
    // cmnFunds.isRequiredUnit only unlocked the unit field below - it never made it mandatory.
    requiredUnitRules(item) {
      return [() => requiredUnitRuleMessage(item, {
        funds: this.funds,
        useNewUnitCodeSetup: this.unitCodeNewMethodFalg,
        isVoid: this.transItem.isVoid,
        isApportionLikeTax: this.transItem.isApportionLikeTax,
      }) || true];
    },
    isRequiredUnitChecked(item) {
      const fundsArray = this.funds;
      for (let i = 0; i < (fundsArray || []).length; i += 1) {
        if ((extractId(item.fund) === extractId(fundsArray[i]))
        && (fundsArray[i].isRequiredUnit === true)) {
          return false;
        }
      }
      return true;
    },
    isAmountReconcilied() {
      return (this.calculatePercentage
         && amountFormat(this.percentageRemaining) === amountFormat(0)
            && ((this.amountRemaining > 0) || this.amountRemaining < 0));
    },
    calculateAmount() {
      for (let i = 0; i < (this.editedItem || []).length; i += 1) {
        const calculatedAmount = (amountRoundOfToClosestDecimal(
          this.editedItem[i].percentageBreakdown, 5,
        )
         * amountToNumber(this.amount)) / 100;
        this.editedItem[i].amount = amountFormat(calculatedAmount);
      }
      this.$nextTick(() => {
        if (this.isAmountReconcilied()) {
          const arrLength = (this.editedItem || []).length;
          const finalAmt = amountToNumber(this.editedItem[arrLength - 1].amount)
           + amountToNumber(this.amountRemaining);
          this.editedItem[arrLength - 1].amount = amountFormat(finalAmt);
          this.somethingChanged();
        }
      });
      return this.editedItem;
    },
    shortcutHandler(e, index) {
      let totalAmount = 0;
      if (isTabOrPlus(e)) {
        if ((index + 1) === (this.editedItem || []).length) {
          for (let i = 0; i < (this.editedItem || []).length; i += 1) {
            totalAmount += amountToNumber(this.editedItem[i].amount);
            this.somethingChanged();
            if (amountToNumber(totalAmount) < amountToNumber(this.amount)) {
              this.addOne();
            }
          }
        }
      }
    },
  },
};
</script>

<style lang="sass">
  .eft-rec-amount input
    text-align: right
  .recon-details-add-new-item
    margin-top: 16px
</style>
